import { NavSectionId } from '../routes';
import { NavIconName } from '../components/icons';
import { v2Path } from '../nav';

/**
 * Data for the landing page's `ProductMock`.
 *
 * ALL OF IT IS FICTIONAL AND DELIBERATELY GENERIC. No real emails, no real
 * phone numbers, no real customer or promoter names, no real event names.
 * Accounts appear only as masked phone fragments. This page must never look
 * like a leaked screenshot of a real account.
 *
 * Cover art is a CSS gradient, not an image: the landing page fires zero
 * network requests, and that includes assets.
 */

export interface MockNavItem {
  id: string;
  path: string;
  /** Localised through the same `app.navigation.*` keys the real sidebar uses. */
  labelKey: string;
  icon: NavIconName;
}

export interface MockNavSection {
  id: NavSectionId;
  labelKey: string;
  items: MockNavItem[];
}

/**
 * Only the three primary sections. The footer zone is left out on purpose: the
 * full rail is ~810px tall and would be cut off inside the mock's frame, which
 * reads as a bug rather than as a design. The mock is decoration
 * (`aria-hidden`), so showing three sections is honest and complete-looking.
 */
export const MOCK_NAV: MockNavSection[] = [
  {
    id: 'find',
    labelKey: 'app.navigation.sections.find',
    items: [
      { id: 'notifications', path: v2Path('/notifications'), labelKey: 'app.navigation.notifications', icon: 'bell' },
      { id: 'waitlist', path: v2Path('/waitlist'), labelKey: 'app.navigation.waitlist', icon: 'list' },
      // Drops is intentionally absent from the marketing mock. The real sidebar
      // shows it to invited accounts; putting it in a public screenshot would
      // advertise the invite-only tier this page must not mention.
    ],
  },
  {
    id: 'inventory',
    labelKey: 'app.navigation.sections.inventory',
    items: [
      { id: 'tickets', path: v2Path('/tickets'), labelKey: 'app.navigation.tickets', icon: 'ticket' },
      { id: 'listings', path: v2Path('/listings'), labelKey: 'app.navigation.listings', icon: 'tag' },
      { id: 'transfers', path: v2Path('/transfers'), labelKey: 'app.navigation.transfers', icon: 'transfer' },
    ],
  },
  {
    id: 'platforms',
    labelKey: 'app.navigation.sections.platforms',
    items: [
      { id: 'accounts', path: v2Path('/accounts'), labelKey: 'app.navigation.connections', icon: 'link' },
      { id: 'dice-settings', path: v2Path('/dice/settings'), labelKey: 'app.navigation.dice', icon: 'phone' },
    ],
  },
];

// ── Panel 1: Inventory, Events view ─────────────────────────────────────────

export interface MockTypeChip {
  label: string;
  transferred?: boolean;
}

export interface MockEvent {
  id: string;
  name: string;
  when: string;
  venue: string;
  /** Tailwind gradient classes standing in for cover art. */
  art: string;
  types: MockTypeChip[];
  count: number;
  transferLabel: string;
  accounts: string[];
}

export const MOCK_EVENTS: MockEvent[] = [
  {
    id: 'e1',
    name: 'Warehouse Project',
    when: 'Fri 14 Aug',
    venue: 'The Old Granary',
    art: 'from-[#3b82f6]/60 to-[#1e3a8a]/60',
    types: [{ label: '3x GA' }, { label: '2x GA (transferred)', transferred: true }],
    count: 5,
    transferLabel: 'Transfer (3)',
    accounts: ['···4821', '···9075'],
  },
  {
    id: 'e2',
    name: 'Printworks Closing',
    when: 'Sat 22 Aug',
    venue: 'Dock Nine',
    art: 'from-violet-500/50 to-fuchsia-900/50',
    types: [{ label: '4x Early Bird' }],
    count: 4,
    transferLabel: 'Transfer (4)',
    accounts: ['···4821'],
  },
  {
    id: 'e3',
    name: 'Basement Sessions',
    when: 'Thu 04 Sep',
    venue: 'Unit 12',
    art: 'from-emerald-500/50 to-teal-900/50',
    types: [{ label: '2x Tier 1' }, { label: '1x Tier 2' }],
    count: 3,
    transferLabel: 'Transfer (3)',
    accounts: ['···9075'],
  },
];

// ── Panel 2: Waitlists ──────────────────────────────────────────────────────

export interface MockTicketType {
  name: string;
  price: string;
  open: boolean;
}

export const MOCK_WAITLIST_EVENT = {
  name: 'Printworks Closing',
  when: 'Sat 22 Aug',
  venue: 'Dock Nine',
  city: 'Manchester',
  art: 'from-violet-500/50 to-fuchsia-900/50',
  types: [
    { name: 'Early Bird', price: '£22.00', open: false },
    { name: 'Tier 1', price: '£28.00', open: true },
    { name: 'Tier 2', price: '£34.00', open: false },
  ] as MockTicketType[],
  joinLabel: 'Join Waitlist (6 accounts)',
};

// ── Panel 3: Transfers ──────────────────────────────────────────────────────

export interface MockTransfer {
  id: string;
  source: string;
  sourceTone: 'blue' | 'violet' | 'amber';
  status: string;
  statusTone: 'green' | 'amber' | 'red';
  platform: string;
  when: string;
  event: string;
  recipient: string;
  qty: string;
  account: string;
}

export const MOCK_TRANSFERS: MockTransfer[] = [
  {
    id: 't1',
    source: 'Flashtools',
    sourceTone: 'blue',
    status: 'Complete',
    statusTone: 'green',
    platform: 'DICE',
    when: '2h ago',
    event: 'Warehouse Project',
    recipient: 'buyer ···4410',
    qty: '2/2',
    account: '···4821',
  },
  {
    id: 't2',
    source: 'Flashticker (sale)',
    sourceTone: 'violet',
    status: 'Complete',
    statusTone: 'green',
    platform: 'DICE',
    when: '5h ago',
    event: 'Printworks Closing',
    recipient: 'buyer ···7732',
    qty: '1/1',
    account: '···9075',
  },
  {
    id: 't3',
    source: 'Flashtools',
    sourceTone: 'blue',
    status: 'Pending',
    statusTone: 'amber',
    platform: 'AXS',
    when: 'Yesterday',
    event: 'Basement Sessions',
    recipient: 'buyer ···1908',
    qty: '0/3',
    account: '···4821',
  },
  {
    id: 't4',
    source: 'Admin',
    sourceTone: 'amber',
    status: 'Failed',
    statusTone: 'red',
    platform: 'TAO',
    when: '2 days ago',
    event: 'Warehouse Project',
    recipient: 'buyer ···6521',
    qty: '0/1',
    account: '···9075',
  },
];
